import { useState } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useFeatures } from './FeatureContext';
import { useTheme } from './ThemeContext';

const TIERS = ['pro', 'team', 'growth', 'business'];

const INDUSTRIES = [
  { value: 'other', label: 'Generic' },
  { value: 'garage_door', label: 'Garage Door' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'pool_service', label: 'Pool Service' },
  { value: 'locksmith', label: 'Locksmith' },
];

export default function DemoTierSwitcher({ companyId, currentTier, currentBusinessType, onTierChanged, onIndustryChanged }) {
  const [collapsed, setCollapsed] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const { refresh: refreshFeatures } = useFeatures();
  const { colors } = useTheme();

  const TIER_MAX_USERS = { pro: 1, team: 3, growth: 5, business: 10 };

  const handleTierSwitch = async (newTier) => {
    if (newTier === currentTier || switching) return;
    setSwitching(true);
    try {
      await supabaseData
        .from('companies')
        .update({ tier: newTier, max_users: TIER_MAX_USERS[newTier] || 5, purchased_seats: 0 })
        .eq('id', companyId);
      if (onTierChanged) onTierChanged(newTier);
      refreshFeatures();
    } catch (err) {
      console.error('Tier switch failed:', err);
    } finally {
      setSwitching(false);
    }
  };

  const handleIndustrySwitch = async (newIndustry) => {
    if (newIndustry === currentBusinessType || reseeding) return;
    if (!window.confirm('Switch industry? This wipes the demo data and reloads with a fresh sample set for the new industry.')) return;
    setReseeding(true);
    try {
      const resp = await fetch('https://workvanapp.com/api/affiliate-demo-reseed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, industry: newIndustry }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.success) {
        alert(data?.error || 'Could not switch industry');
        return;
      }
      if (onIndustryChanged) onIndustryChanged(newIndustry);
    } catch (err) {
      alert('Industry switch failed: ' + err.message);
    } finally {
      setReseeding(false);
    }
  };

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          zIndex: 99998,
          padding: '0.5rem 1rem',
          background: colors.accent,
          color: '#fff',
          border: 'none',
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          letterSpacing: '0.5px',
          boxShadow: '0 2px 8px rgba(240, 90, 40, 0.4)'
        }}
      >
        DEMO
      </button>
    );
  }

  const sectionLabel = {
    color: '#888', fontSize: '0.6rem', fontWeight: 'bold', letterSpacing: '1px',
    textTransform: 'uppercase', marginBottom: '0.3rem',
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      zIndex: 99998,
      background: '#1a1a1a',
      border: '1px solid #F05A28',
      borderRadius: '12px',
      padding: '0.75rem 1rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
      minWidth: '260px',
      maxWidth: '320px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ color: '#F05A28', fontSize: '0.7rem', fontWeight: 'bold', letterSpacing: '1px' }}>
          DEMO MODE
        </span>
        <button
          onClick={() => setCollapsed(true)}
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem', lineHeight: 1 }}
        >
          ✕
        </button>
      </div>

      {/* Tier */}
      <div style={sectionLabel}>Plan tier</div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0.6rem' }}>
        {TIERS.map(tier => (
          <button
            key={tier}
            onClick={() => handleTierSwitch(tier)}
            disabled={switching || reseeding}
            style={{
              flex: 1,
              padding: '0.4rem 0.25rem',
              background: currentTier === tier ? colors.accent : '#2a2a2a',
              color: currentTier === tier ? '#fff' : '#888',
              border: currentTier === tier ? `1px solid ${colors.accent}` : '1px solid #3a3a3a',
              borderRadius: '6px',
              fontSize: '0.65rem',
              fontWeight: currentTier === tier ? 'bold' : 'normal',
              cursor: (switching || reseeding) ? 'wait' : 'pointer',
              textTransform: 'capitalize',
              transition: 'all 0.15s'
            }}
          >
            {tier === 'business' ? 'Biz' : tier.charAt(0).toUpperCase() + tier.slice(1)}
          </button>
        ))}
      </div>

      {/* Industry */}
      <div style={sectionLabel}>Industry</div>
      <select
        value={INDUSTRIES.find(i => i.value === currentBusinessType) ? currentBusinessType : 'other'}
        onChange={(e) => handleIndustrySwitch(e.target.value)}
        disabled={reseeding || switching}
        style={{
          width: '100%',
          padding: '0.4rem 0.5rem',
          background: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '6px',
          color: '#e0e0e0',
          fontSize: '0.75rem',
          cursor: reseeding ? 'wait' : 'pointer',
        }}
      >
        {INDUSTRIES.map(i => (
          <option key={i.value} value={i.value}>{i.label}</option>
        ))}
      </select>
      {reseeding && (
        <div style={{ color: '#F05A28', fontSize: '0.7rem', marginTop: '0.4rem', textAlign: 'center' }}>
          Refreshing demo data…
        </div>
      )}
    </div>
  );
}
